<template>
    <edit-resource
        :form="form"
        :title="title"
        @close="$emit('close')"
        @submit="saveValue"
    >
        <div class="w-768 flex">
            <div class="w-1/2 flex items-center justify-center mb-32 mr-32">
                <div class="w-full h-full">
                    <siu
                        v-model="form.photo_path_queued_id"
                        :image="imageUrl"
                        class="h-full rounded shadow-md"
                        classes="h-full"
                        icon-location="corner"
                        @clear="clearPhoto"
                        @success="resetPhoto">
                    </siu>
                </div>

            </div>
            <div class="w-1/2">
                <translatable #default="{ languages, selectedLanguage }" :errors="warningTabs" class="mb-32">
                    <div v-for="lang in languages" :key="lang">
                        <simple-input
                            v-show="lang === selectedLanguage"
                            v-model="form.title[lang]"
                            class="mb-16">
                            {{ $t('form.title') }} <span v-if="languages.length > 1">({{ lang }})</span>
                        </simple-input>

                        <simple-textarea
                            v-show="lang === selectedLanguage"
                            v-model="form.description[lang]"
                            full-featured>
                            {{ $t('form.description') }} <span v-if="languages.length > 1">({{ lang }})</span>
                        </simple-textarea>
                    </div>
                </translatable>
            </div>
        </div>
        <div class="w-768">
            <div class="pt-4 mb-16">
                <toggle v-model="transChecked">
                    {{ $t('site_editor.video.per_language') }}
                </toggle>
            </div>

            <div v-show="transChecked">
                <translatable #default="{ languages, selectedLanguage }" :errors="warningTabsUrl" class="w-full">
                    <div v-for="lang in languages" :key="lang">
                        <preview-video
                            v-show="lang === selectedLanguage"
                            :language="lang"
                            :value="form.translated_video_url[lang]"
                            @update="updateVideoUrl($event, lang)">
                        </preview-video>
                    </div>
                </translatable>
            </div>

            <div v-show="!transChecked">
                <preview-video
                    :value="form.video_url"
                    @update="updateVideoUrl($event)">
                </preview-video>
            </div>
        </div>
    </edit-resource>
</template>

<script>
import EditResource from './EditResource.vue';
import Input from '../forms/Input.vue';
import Translatable from '../forms/Translatable.vue';
import Value from '../../models/Value';
import * as values from '../../services/ValueService';
import _mapValues from 'lodash/mapValues';
import _isEqual from 'lodash/isEqual';
import Form from '../../etc/forms/Form';
import {normalizeJson} from '../../../shared/helpers';
import PreviewVideo from './PreviewVideo.vue';
import SingleImageUpload from '../forms/SingleImageUpload.vue';
import TextArea from '../forms/TextArea.vue';
import Toggle from '../forms/Toggle.vue';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';

function getFields() {
    return [
        {name: 'title', value: {}, rules: ['required']},
        {name: 'description', value: {}, rules: ['required']},
        {name: 'video_url', value: ''},
        {name: 'translated_video_url', value: {}},
        {name: 'photo_path', value: 'default'},
        {name: 'photo_path_queued_id', value: null},
    ];
}

export default {
    components: {
        'edit-resource': EditResource,
        'preview-video': PreviewVideo,
        'simple-input': Input,
        'translatable': Translatable,
        'siu': SingleImageUpload,
        'simple-textarea': TextArea,
        'toggle': Toggle,
    },
    props: {
        value: Object,
    },
    data: function () {
        return {
            form: this.generateForm(),
            tmpTransUrls: {},
            transChecked: false,
            savingValue: false,
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        title() {
            return this.$t(
                this.value ? 'general.edit_subject' : 'general.add_subject',
                {subject: this.$t('site_editor.values.single')},
            );
        },
        imageUrl: {
            get() {
                return (typeof this.value !== 'undefined') ? this.value.image_url : null;
            },
        },
        warningTabs() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.title[languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
        warningTabsUrl() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.translated_video_url[languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
    },
    methods: {
        generateForm() {
            let fields = getFields();
            if (this.value) {
                fields.forEach((field, index) => {
                    if (this.value.hasOwnProperty(field.name)) {
                        fields[index].value = JSON.parse(JSON.stringify(this.value[field.name]));
                    }
                });
            }
            return new Form(fields);
        },
        async saveValue() {
            if (this.form.data.photo_path_queued_id === null) {
                delete this.form.data.photo_path_queued_id;
            }

            if (this.value && this.value.id) {
                await this.updateValue();
            } else {
                let value = await this.createValue();
                if (value) {
                    this.$emit('created', value.id);
                }
            }

            this.$emit('close');
        },
        updateValue() {
            if (this.form.data.photo_path === 'default') {
                delete this.form.data.photo_path;
            }

            return this.form.submit(data => values.update(this.value.id, {
                data: {
                    type: 'values',
                    attributes: data,
                },
            })).then(result => {
                return useRepo(Value).save(normalizeJson(result));
            });
        },
        updateVideoUrl(value, locale) {
            if (typeof locale != 'undefined') {
                this.form.translated_video_url[locale] = value;
            } else {
                this.form.video_url = value;
            }
        },
        createValue() {
            delete this.form.data.photo_path;

            return this.form.submit(data => values.store({
                data: {
                    type: 'values',
                    attributes: data,
                },
            })).then(result => {
                return useRepo(Value).save(normalizeJson(result));
            });
        },
        clearPhoto() {
            this.form.data.photo_path = null;
        },
        resetPhoto() {
            this.form.data.photo_path = 'default';
        },
    },
    created() {
        for (let lang of Object.keys(this.form.translated_video_url)) {
            this.tmpTransUrls[lang] = this.form.translated_video_url[lang];

            if (this.form.translated_video_url[lang]) {
                this.transChecked = true;
            }
        }
    },
    watch: {
        transChecked(value, oldValue) {
            if (value) {
                //re-populate lang form inputs
                for (let lang of Object.keys(this.tmpTransUrls)) {
                    this.form.translated_video_url[lang] = this.tmpTransUrls[lang];
                }
            } else {
                //hold lang form inputs values and empty them
                for (let lang of Object.keys(this.form.translated_video_url)) {
                    this.tmpTransUrls[lang] = this.form.translated_video_url[lang];
                    this.form.translated_video_url[lang] = null;
                }
            }
        },
    },
};
</script>
